<template>
    <div v-if="event" class="space-y-4">
        <FlexibleCard headline-id="documents" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('documents')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('documents')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('documents')"
                    @enter-edit-mode="onEnterEditMode('documents', $t('event-annual-documents'))"
                    @leave-edit-mode="leaveEditMode('documents')"
                    @discard-changes="discardChanges('documents')"
                    @save="saveForm('documents')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-documents-help')">
                        {{ $t('event-annual-documents') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventDocumentsForm v-if="showForm('documents')" ref="eventDocumentsFormRef" class="p-5" />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="extraDocuments.length"
                            :label="$t('extra-documents')"
                            :items="extraDocuments"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('extra-documents')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="systems" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('systems')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('systems')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('systems')"
                    @enter-edit-mode="onEnterEditMode('systems', $t('event-annual-systems'))"
                    @leave-edit-mode="leaveEditMode('systems')"
                    @discard-changes="discardChanges('systems')"
                    @save="saveForm('systems')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-systems-help')">
                        {{ $t('event-annual-systems') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSystemsForm
                        v-if="showForm('systems')"
                        ref="eventSystemsFormRef"
                        class="p-5"
                        :event-id="event.id"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="extraSystems.length"
                            :label="$t('extra-systems')"
                            :items="extraSystems"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('extra-systems')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="data" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('data')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('data')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('data')"
                    @enter-edit-mode="onEnterEditMode('data', $t('event-annual-data'))"
                    @leave-edit-mode="leaveEditMode('data')"
                    @discard-changes="discardChanges('data')"
                    @save="saveForm('data')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('annual-data-help')">
                        {{ $t('event-annual-data') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventDataForm v-if="showForm('data')" ref="eventDataFormRef" class="p-5" :event-id="event.id" />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="externalData.length"
                            :label="$t('external-data')"
                            :items="externalData"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('external-data')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="internalData.length"
                            :label="$t('internal-data')"
                            :items="internalData"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('internal-data')" text="-" :has-border-bottom="false" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="subevents" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('subevents')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('subevents')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('subevents')"
                    @enter-edit-mode="onEnterEditMode('subevents', $t('event-subevents'))"
                    @leave-edit-mode="leaveEditMode('subevents')"
                    @discard-changes="discardChanges('subevents')"
                    @save="saveForm('subevents')"
                >
                    <CardHeadline :headline-id="headlineId" :help-text="$t('event-subevents-help')">
                        {{ $t('event-subevents') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventSubEventsForm
                        v-if="showForm('subevents')"
                        ref="eventSubEventsFormRef"
                        class="p-5"
                        :event-id="event.id"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="subeventsLocal.length"
                            :label="$t('subevents-local')"
                            :items="subeventsLocal"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('subevents-local')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="subeventsOtherLocations.length"
                            :label="$t('subevents-other-locations')"
                            :items="subeventsOtherLocations"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('subevents-other-locations')"
                            text="-"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="fair" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('fair')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('fair')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('fair')"
                    @enter-edit-mode="onEnterEditMode('fair', $t('event-annual-fair-abroad'))"
                    @leave-edit-mode="leaveEditMode('fair')"
                    @discard-changes="discardChanges('fair')"
                    @save="saveForm('fair')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('event-annual-fair-abroad') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventFairAbroadForm
                        v-if="showForm('fair')"
                        ref="eventFairAbroadFormRef"
                        class="p-5"
                        :event-id="event.id"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="fairAbroad.length"
                            :label="$t('fair-abroad')"
                            :items="fairAbroad"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('fair-abroad')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="fairAbroadCooperation.length"
                            :label="$t('fair-abroad-cooperation')"
                            :items="fairAbroadCooperation"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('fair-abroad-cooperation')"
                            text="-"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="competition" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('competition')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('competition')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('competition')"
                    @enter-edit-mode="onEnterEditMode('competition', $t('event-annual-competition'))"
                    @leave-edit-mode="leaveEditMode('competition')"
                    @discard-changes="discardChanges('competition')"
                    @save="saveForm('competition')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('event-annual-competition') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventCompetitionForm
                        v-if="showForm('competition')"
                        ref="eventCompetitionFormRef"
                        class="p-5"
                        :event-id="event.id"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="competitionNational.length"
                            :label="$t('competition-national-label')"
                            :items="competitionNational"
                            :has-border-bottom="true"
                            :help-text="$t('competition-national-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('competition-national-label')"
                            text="-"
                            :has-border-bottom="true"
                            :help-text="$t('competition-national-help')"
                        />
                        <CardRowAttachmentGroup
                            v-if="otherCompetitionNational.length"
                            :label="$t('other-competition-national')"
                            :items="otherCompetitionNational"
                            :has-border-bottom="true"
                            :help-text="$t('other-competition-national-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('other-competition-national')"
                            text="-"
                            :has-border-bottom="true"
                            :help-text="$t('other-competition-national-help')"
                        />
                        <CardRowAttachmentGroup
                            v-if="competitionInternational.length"
                            :label="$t('competition-international-label')"
                            :items="competitionInternational"
                            :has-border-bottom="true"
                            :help-text="$t('competition-international-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('competition-international-label')"
                            text="-"
                            :has-border-bottom="true"
                            :help-text="$t('competition-international-help')"
                        />
                        <CardRowAttachmentGroup
                            v-if="otherCompetitionInternational.length"
                            :label="$t('other-competition-international')"
                            :items="otherCompetitionInternational"
                            :has-border-bottom="true"
                            :help-text="$t('other-competition-international-help')"
                        />
                        <CardRowText
                            v-else
                            :label="$t('other-competition-international')"
                            text="-"
                            :help-text="$t('other-competition-international-help')"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="parallels" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('parallels')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('parallels')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('parallels')"
                    @enter-edit-mode="onEnterEditMode('parallels', $t('event-annual-favored-parallels'))"
                    @leave-edit-mode="leaveEditMode('parallels')"
                    @discard-changes="discardChanges('parallels')"
                    @save="saveForm('parallels')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('event-annual-favored-parallels') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventParallelsForm
                        v-if="showForm('parallels')"
                        ref="eventParallelsFormRef"
                        class="p-5"
                        :event-id="event.id"
                    />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="favoredParallelsInternal.length"
                            :label="$t('favored-parallels-internal')"
                            :items="favoredParallelsInternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('favored-parallels-internal')"
                            text="-"
                            :has-border-bottom="true"
                        />
                        <CardRowAttachmentGroup
                            v-if="favoredParallelsExternal.length"
                            :label="$t('favored-parallels-external')"
                            :items="favoredParallelsExternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            v-else
                            :label="$t('favored-parallels-external')"
                            text="-"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>

        <FlexibleCard headline-id="otherLinks" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus('otherLinks')"
                    :edit-button-disabled-explanation="editButtonDisabledMessage('otherLinks')"
                    :save-button-disabled-explanation="saveButtonDisabledMessage('otherLinks')"
                    @enter-edit-mode="onEnterEditMode('otherLinks', $t('event-annual-other-links'))"
                    @leave-edit-mode="leaveEditMode('otherLinks')"
                    @discard-changes="discardChanges('otherLinks')"
                    @save="saveForm('otherLinks')"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ $t('event-annual-other-links') }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>

            <template #body>
                <ContentFormTransition>
                    <EventOtherLinksForm v-if="showForm('otherLinks')" ref="eventOtherLinksFormRef" class="p-5" />
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="otherLinksInternal.length"
                            :label="$t('other-links-internal')"
                            :items="otherLinksInternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('other-links-internal')" text="-" :has-border-bottom="true" />
                        <CardRowAttachmentGroup
                            v-if="otherLinksExternal.length"
                            :label="$t('other-links-external')"
                            :items="otherLinksExternal"
                            :has-border-bottom="true"
                        />
                        <CardRowText v-else :label="$t('other-links-external')" text="-" :has-border-bottom="true" />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>

<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowAttachmentGroup from '@/components/Card/CardRowAttachmentGroup.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import { toCardRowAttachmentLink } from '@/components/MultiLink/multilink';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useEventInjection } from '@/event/composables/useEventInjection';
import { useActiveCard } from '@/forms/composables/useActiveCard';

import EventCompetitionForm from '@/event/forms/event/EventCompetitionForm.vue';
import EventDataForm from '@/event/forms/event/EventDataForm.vue';
import EventDocumentsForm from '@/event/forms/event/EventDocumentsForm.vue';
import EventFairAbroadForm from '@/event/forms/event/EventFairAbroadForm.vue';
import EventOtherLinksForm from '@/event/forms/event/EventOtherLinksForm.vue';
import EventParallelsForm from '@/event/forms/event/EventParallelsForm.vue';
import EventSubEventsForm from '@/event/forms/event/EventSubEventsForm.vue';
import EventSystemsForm from '@/event/forms/event/EventSystemsForm.vue';
import { useFormRegistry } from '@/event/forms/useFormRegistry';
import { useFluent } from 'fluent-vue';
import { computed, useTemplateRef } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const { event } = useEventInjection();

const fluent = useFluent();

const {
    activeCard,
    resetActiveCard,
    onEnterEditMode,
    getCardStatus,
    showForm,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
} = useActiveCard();

const extraDocuments = computed(() => toCardRowAttachmentLink(event.value?.extraDocuments?.links?.nodes));
const extraSystems = computed(() => toCardRowAttachmentLink(event.value?.extraSystems?.links?.nodes));
const internalData = computed(() => toCardRowAttachmentLink(event.value?.internalData?.links?.nodes));
const externalData = computed(() => toCardRowAttachmentLink(event.value?.externalData?.links?.nodes));
const subeventsLocal = computed(() => toCardRowAttachmentLink(event.value?.subeventsLocal?.links?.nodes));
const subeventsOtherLocations = computed(() =>
    toCardRowAttachmentLink(event.value?.subeventsOtherLocations?.links?.nodes),
);
const fairAbroad = computed(() => toCardRowAttachmentLink(event.value?.fairAbroad?.links?.nodes));
const fairAbroadCooperation = computed(() => toCardRowAttachmentLink(event.value?.fairAbroadCooperation?.links?.nodes));
const competitionNational = computed(() => toCardRowAttachmentLink(event.value?.competitionNational?.links?.nodes));
const otherCompetitionNational = computed(() =>
    toCardRowAttachmentLink(event.value?.otherCompetitionNational?.links?.nodes),
);
const competitionInternational = computed(() =>
    toCardRowAttachmentLink(event.value?.competitionInternational?.links?.nodes),
);
const otherCompetitionInternational = computed(() =>
    toCardRowAttachmentLink(event.value?.otherCompetitionInternational?.links?.nodes),
);
const favoredParallelsInternal = computed(() =>
    toCardRowAttachmentLink(event.value?.favoredParallelsInternal?.links?.nodes),
);
const favoredParallelsExternal = computed(() =>
    toCardRowAttachmentLink(event.value?.favoredParallelsExternal?.links?.nodes),
);
const otherLinksInternal = computed(() => toCardRowAttachmentLink(event.value?.otherLinksInternal?.links?.nodes));
const otherLinksExternal = computed(() => toCardRowAttachmentLink(event.value?.otherLinksExternal?.links?.nodes));

const eventSeriesDocumentsFormRef = useTemplateRef('eventDocumentsFormRef');
const eventSeriesSystemsFormRef = useTemplateRef('eventSystemsFormRef');
const eventSeriesDataFormRef = useTemplateRef('eventDataFormRef');
const eventSeriesSubEventsForm = useTemplateRef('eventSubEventsFormRef');
const eventSeriesFairAbroadFormRef = useTemplateRef('eventFairAbroadFormRef');
const eventSeriesCompetitionFormRef = useTemplateRef('eventCompetitionFormRef');
const seriesParallelsFormRef = useTemplateRef('eventParallelsFormRef');
const eventSeriesOtherLinksFormRef = useTemplateRef('eventOtherLinksFormRef');

const { register, saveForm, discardChanges, leaveEditMode, resetAll } = useFormRegistry();

register('documents', eventSeriesDocumentsFormRef);
register('systems', eventSeriesSystemsFormRef);
register('data', eventSeriesDataFormRef);
register('subevents', eventSeriesSubEventsForm);
register('fair', eventSeriesFairAbroadFormRef);
register('competition', eventSeriesCompetitionFormRef);
register('parallels', seriesParallelsFormRef);
register('otherLinks', eventSeriesOtherLinksFormRef);

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        resetAll();
    }
    resetActiveCard();
    return true;
});
</script>
